import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Card, Descriptions, Tag, Button, Select, Space, Spin, Modal, message, Image } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, SaveOutlined, SoundOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAudioAdmin } from '../../graphql/queries';
import { updateAudioStatus, deleteAudioAdmin } from '../../graphql/mutation';
import './AudioDetail.css';

const { Option } = Select;
const { confirm } = Modal;

const STATUS_COLORS = {
  SUCCEEDED: 'success',
  FAILED: 'error',
  RUNNING: 'processing',
  IN_QUE: 'warning',
  PENDING: 'default',
  THROTTLED: 'purple'
};

const SOURCE_COLORS = {
  FAL_AI: 'blue',
  KIE_AI: 'green'
};

const AudioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState(null);

  const { loading, data, refetch } = useQuery(getAudioAdmin, {
    variables: { audioId: id },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getAudioAdmin?.status) {
        setSelectedStatus(data.getAudioAdmin.status);
      }
    }
  });

  const [updateStatus, { loading: updating }] = useMutation(updateAudioStatus, {
    onCompleted: () => {
      message.success('Status updated successfully');
      refetch();
    },
    onError: (error) => {
      message.error(`Failed to update status: ${error.message}`);
    }
  });

  const [deleteAudio, { loading: deleting }] = useMutation(deleteAudioAdmin, {
    onCompleted: () => {
      message.success('Audio deleted successfully');
      navigate('/audios');
    },
    onError: (error) => {
      message.error(`Failed to delete audio: ${error.message}`);
    }
  });

  const handleStatusUpdate = () => {
    if (selectedStatus && selectedStatus !== audio.status) {
      updateStatus({
        variables: {
          audioId: id,
          status: selectedStatus
        }
      });
    }
  };

  const handleDelete = () => {
    confirm({
      title: 'Delete Audio',
      content: 'Are you sure you want to delete this audio? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteAudio({ variables: { audioId: id } });
      }
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="audio-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  const audio = data?.getAudioAdmin;

  if (!audio) {
    return (
      <div className="audio-detail-container">
        <Card>
          <p>Audio not found</p>
          <Button onClick={() => navigate('/audios')}>Back to Audios</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="audio-detail-container">
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/audios')}>
              Back
            </Button>
            <span>Audio Detail</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              value={selectedStatus}
              style={{ width: 140 }}
              onChange={setSelectedStatus}
            >
              <Option value="SUCCEEDED">Succeeded</Option>
              <Option value="FAILED">Failed</Option>
              <Option value="RUNNING">Running</Option>
              <Option value="IN_QUE">In Queue</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="THROTTLED">Throttled</Option>
            </Select>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleStatusUpdate}
              loading={updating}
              disabled={selectedStatus === audio.status}
            >
              Update Status
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleting}
            >
              Delete
            </Button>
          </Space>
        }
      >
        <div className="audio-detail-content">
          <div className="audio-player-section">
            {audio.imageUrl && (
              <div className="audio-cover">
                <Image
                  src={audio.imageUrl}
                  alt="Cover"
                  width={200}
                  height={200}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
              </div>
            )}

            {audio.url ? (
              <div className="audio-player">
                <audio controls style={{ width: '100%' }}>
                  <source src={audio.url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            ) : (
              <div className="audio-placeholder-large">
                <SoundOutlined />
                <span>No audio available</span>
              </div>
            )}

            {audio.streamAudioUrl && (
              <div className="stream-audio">
                <p><strong>Stream URL:</strong></p>
                <audio controls style={{ width: '100%' }}>
                  <source src={audio.streamAudioUrl} type="audio/mpeg" />
                </audio>
              </div>
            )}
          </div>

          <Descriptions bordered column={2} className="audio-info">
            <Descriptions.Item label="ID" span={2}>{audio.id}</Descriptions.Item>
            <Descriptions.Item label="Title">{audio.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[audio.status]}>{audio.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Generation Source">
              <Tag color={SOURCE_COLORS[audio.generationSource]}>{audio.generationSource}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Duration">{formatDuration(audio.duration)}</Descriptions.Item>
            <Descriptions.Item label="Progress">{audio.progressRatio !== null ? `${audio.progressRatio}%` : '-'}</Descriptions.Item>
            <Descriptions.Item label="Format">{audio.format || '-'}</Descriptions.Item>
            <Descriptions.Item label="Sample Rate">{audio.sampleRate || '-'}</Descriptions.Item>
            <Descriptions.Item label="Bitrate">{audio.bitrate || '-'}</Descriptions.Item>
            <Descriptions.Item label="User ID">{audio.userId || '-'}</Descriptions.Item>
            <Descriptions.Item label="User Email">{audio.user?.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="RC User ID">{audio.user?.rcUserId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Task ID" span={2}>{audio.taskId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Created At">{dayjs(audio.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
            <Descriptions.Item label="Updated At">{dayjs(audio.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
            <Descriptions.Item label="Prompt" span={2}>
              <div className="prompt-text">{audio.prompt || '-'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Lyrics Prompt" span={2}>
              <div className="prompt-text">{audio.lyricsPrompt || '-'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Tags" span={2}>{audio.tags || '-'}</Descriptions.Item>
            {audio.errorReason && (
              <Descriptions.Item label="Error Reason" span={2}>
                <span className="error-text">{audio.errorReason}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      </Card>
    </div>
  );
};

export default AudioDetail;
